<template>
  <div>
    <van-popup
      :show="show"
      position="bottom"
      round
      :style="{ height: 'auto' }"
      @update:show="emit('update:show', $event)"
    >
      <div class="receipt-dialog">
        <div class="dialog-header">
          <span class="dialog-title">收据</span>
          <van-icon name="cross" size="20" @click="emit('update:show', false)" />
        </div>

        <div class="dialog-body">
          <!-- 生成中 -->
          <div v-if="generating" class="state-area">
            <van-loading type="spinner" color="#1989fa" size="28" />
            <span>收据图片生成中...</span>
          </div>

          <!-- 生成失败 -->
          <div v-else-if="failed" class="state-area clickable" @click="generate">
            <van-icon name="replay" size="30" color="#969799" />
            <span>生成失败，点击重试</span>
          </div>

          <!-- 成品 -->
          <template v-else-if="receiptImg">
            <img class="receipt-img" :src="receiptImg" alt="收据" @click="preview" />
            <div class="receipt-hint">
              <van-icon name="info-o" size="13" />
              <span>点击图片可放大查看；长按图片可保存或转发给家长</span>
            </div>
          </template>
        </div>
      </div>
    </van-popup>

    <!-- 离屏收据画布：固定 1100px 宽，与 PC 打印版同一套坐标。
         截图只对这块画布进行（scale:2），输出约 2200px 宽，
         任何手机生成的图片完全一致，与设备分辨率无关。
         首次打开才挂载，避免列表页加载收据底图资源。 -->
    <div v-if="mounted" ref="receiptWrapRef" class="receipt-wrap">
      <div class="receipt-canvas">
        <img :src="form.companyId === 2 ? billBsImg : billImg" alt="" class="bill">
        <span class="field studentName">{{ form.studentName }}</span>
        <span class="field payTime">{{ form.payTime }}</span>
        <span class="field contractNo">{{ form.contractNo }}</span>
        <span class="field studentNo">{{ form.studentNo }}</span>
        <span class="field orderType">{{ form.type }}</span>
        <span class="field schoolName">{{ form.schoolName }}</span>
        <span class="field makeBill">{{ form.createUserName }}</span>
        <span class="field payee">{{ form.createUserName }}</span>
        <span class="field receiptNumber">{{ form.receiptNo }}</span>

        <table class="main-table">
          <tr>
            <td>学科</td>
            <td>业务类型</td>
            <td>年级</td>
            <td>原价</td>
            <td>购买课时</td>
            <td>赠送课时</td>
            <td>总课时</td>
            <td>优惠金额</td>
            <td>原价</td>
            <td>优惠后价格</td>
          </tr>
          <tr v-for="course in form.studyCourseList" :key="course.orderCourseId">
            <td>{{ selectDictLabel(course_subject, course.subject) }}</td>
            <td>{{ selectDictLabel(teaching_model, course.teachingModel) }}</td>
            <td>{{ selectDictLabel(grade_list, course.grade) }}</td>
            <td>{{ course.totalPrice }}</td>
            <td>{{ course.buyClassHourCount }}</td>
            <td>{{ course.giftClassHourCount }}</td>
            <td>{{ Number(course.giftClassHourCount || 0) + Number(course.buyClassHourCount || 0) }}</td>
            <td>{{ parseFloat(((Number(course.coursePay) || 0) - (Number(course.realPrice) || 0)).toFixed(2)) }}</td>
            <td>{{ course.coursePay }}</td>
            <td>{{ course.realPrice }}</td>
          </tr>

          <tr v-if="form.filingAmount > 0">
            <td>建档费</td>
            <td colspan="9"></td>
            <td>{{ form.filingAmount }}</td>
          </tr>

          <tr v-if="form.textBookAmount > 0">
            <td>教材费</td>
            <td colspan="9"></td>
            <td>{{ form.textBookAmount }}</td>
          </tr>

          <tr v-if="form.orderSupplements && form.orderSupplements.length > 0">
            <td>定&nbsp;金</td>
            <td colspan="2">缴费时间：{{ form.payTime }}</td>
            <td colspan="6"></td>
            <td>{{ form.depositAmount }}</td>
          </tr>

          <tr v-for="item in form.orderSupplements" :key="item.orderSupplementId">
            <td>补&nbsp;交</td>
            <td colspan="4">补交时间：{{ item.supplementDate }}</td>
            <td colspan="4"></td>
            <td>{{ item.supplementAmount }}</td>
          </tr>
        </table>

        <div class="bottom">
          <div>
            <label>应收金额：¥{{ form.shouldPay }}</label>
            <label class="second" v-if="!form.newOrderSupplement">实收金额：<span>{{ form.realPayChineseAmount }}(小写：¥{{ form.realPay }})</span></label>
            <label class="second" v-if="form.newOrderSupplement">本次实收：<span>{{ form.realPayChineseAmount }}(小写：¥{{ form.newOrderSupplement?.supplementAmount }})</span></label>
            <label class="second" v-if="form.arrearage > 0">欠费：¥{{ form.arrearage }}</label>
          </div>
          <div v-if="form.realPay > 0 || form.newOrderSupplement?.supplementAmount > 0">
            <label>支付方式:</label><span>{{ form.payType }}</span>
          </div>
        </div>

        <!-- 公章：透明底 PNG，盖在收款人/签名确认位置 -->
        <img class="seal" :class="{ 'seal-bs': form.companyId === 2 }"
          :src="form.companyId === 2 ? sealBsImg : sealImg" alt="" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import { showImagePreview } from 'vant'
import { getReceiptInfo } from '@/api/order'
import { useDict, selectDictLabel } from '@/utils/dict'
import billImg from '@/assets/images/bill.png'
import billBsImg from '@/assets/images/bill-bs.png'
import sealImg from '@/assets/images/seal.png'
import sealBsImg from '@/assets/images/seal-bs.png'

const props = defineProps({
  show: Boolean,
  orderId: [String, Number]
})
const emit = defineEmits(['update:show'])

const { course_subject, teaching_model, grade_list } = useDict('course_subject', 'teaching_model', 'grade_list')

const receiptWrapRef = ref(null)
const form = ref({})
const mounted = ref(false)
const generating = ref(false)
const failed = ref(false)
const receiptImg = ref('')

// 同一订单截图结果缓存，重复打开秒出
const imgCache = {}

watch(() => props.show, val => {
  if (val && props.orderId) {
    mounted.value = true
    generate()
  }
})

async function generate() {
  const cacheKey = String(props.orderId)
  if (imgCache[cacheKey]) {
    receiptImg.value = imgCache[cacheKey]
    failed.value = false
    return
  }
  generating.value = true
  failed.value = false
  receiptImg.value = ''
  try {
    const res = await getReceiptInfo(props.orderId)
    // H5 request 拦截器已解包 response.data.data，res 直接就是收据数据
    form.value = res || {}
    // 等画布挂载 + 底图/公章加载完成后再截图
    await nextTick()
    const imgs = receiptWrapRef.value ? receiptWrapRef.value.querySelectorAll('img') : []
    await Promise.all(Array.from(imgs).map(img =>
      img.complete ? Promise.resolve() : new Promise(resolve => { img.onload = resolve; img.onerror = resolve })
    ))
    await nextTick()
    const { default: html2canvas } = await import('html2canvas')
    const canvas = await html2canvas(receiptWrapRef.value, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff'
    })
    const url = canvas.toDataURL('image/png')
    receiptImg.value = url
    imgCache[cacheKey] = url
  } catch (e) {
    failed.value = true
  } finally {
    generating.value = false
  }
}

/** 全屏预览，支持双指缩放核对金额 */
function preview() {
  showImagePreview({ images: [receiptImg.value], closeable: true })
}
</script>

<style scoped>
.receipt-dialog {
  padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
}

.state-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 0;
  color: #969799;
  font-size: 14px;
}

.state-area.clickable {
  cursor: pointer;
}

.receipt-img {
  display: block;
  width: 100%;
  border: 1px solid #ebedf0;
  border-radius: 6px;
}

.receipt-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 10px;
  font-size: 12px;
  color: #969799;
}

/* ==================== 离屏收据画布（与 PC receiptPrinter 同一套坐标） ==================== */

/* 外层：被截图的容器。padding-top 20px 抵消 bill 的 top:-20px，
   保证底图完整落在截图窗口内（窗口 1100x620） */
.receipt-wrap {
  position: fixed;
  left: -99999px;
  top: 0;
  width: 1100px;
  height: 620px;
  padding-top: 20px;
  box-sizing: border-box;
  background: #fff;
  color: #000;
  font-size: 16px;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
}

.receipt-canvas {
  position: relative;
  width: 1100px;
  height: 600px;
  background: #fff;
}

.bill {
  width: 1100px;
  height: auto;
  position: absolute;
  top: -20px;
  left: 0;
}

.field {
  position: absolute;
  z-index: 0;
  color: #000;
}

/* 坐标：与 PC 端一致（bill.png 缩放到 1100px 宽后的 OCR 换算） */
.studentName { left: 165px; top: 64px; }
.payTime { left: 455px; top: 64px; }
.contractNo { left: 745px; top: 64px; }
.studentNo { left: 165px; top: 85px; }
.orderType { left: 455px; top: 83px; }
.schoolName { left: 745px; top: 83px; }
.receiptNumber { left: 870px; top: 32px; }
.makeBill { left: 166px; bottom: 82px; }
.payee { left: 598px; bottom: 82px; }

.main-table {
  position: absolute;
  top: 130px;
  left: 130px;
  width: 850px;
}

.main-table td {
  text-align: left;
  padding: 0;
}

.bottom {
  position: absolute;
  bottom: 160px;
  left: 130px;
}

.bottom label {
  margin-right: 10px;
  font-weight: bold;
}

.bottom div {
  margin-bottom: 10px;
}

.second {
  margin-left: 60px;
}

/* 公章：盖在收款人/签名确认位置 */
.seal {
  position: absolute;
  height: 120px;
  right: 230px;
  bottom: 100px;
  z-index: 10;
  pointer-events: none;
}

/* 博思公章单独调大 */
.seal-bs {
  height: 150px;
}
</style>
